import intl from 'react-intl-universal';
import { LOCALE_KEY, locales } from './constants';
import { allLangs, defaultLang } from './langConfig';
import { AppLocale, TranslationKey, TransParams } from './types';

export const getCurrentLocale = (): AppLocale => {
  return (localStorage?.getItem(LOCALE_KEY) as AppLocale) ?? defaultLang.value;
};

/**
 * The locale somebody actually chose, or null when this browser has never been told.
 *
 * `getCurrentLocale` cannot answer this — it falls back to the default, so "never asked" and "chose
 * German" come back as the same value. The difference decides whether it is safe to adopt the
 * device's language: doing so instead of a default is helpful, doing so over an explicit choice is
 * overriding somebody.
 */
export const getStoredLocale = (): AppLocale | null => {
  // Validated, not cast. `setCurrentLocale` takes a plain string and localStorage is editable by
  // anyone, so the stored value is not known to be an AppLocale until it is checked against one —
  // and a function whose type says otherwise hands every caller a lie.
  const stored = localStorage?.getItem(LOCALE_KEY);
  return Object.values(AppLocale).includes(stored as AppLocale)
    ? (stored as AppLocale)
    : null;
};

export const getApiLocale = (locale?: AppLocale): string => {
  const currentLocale = locale || getCurrentLocale();
  return currentLocale.split('-')[0];
};

export const setCurrentLocale = (locale: string): void => {
  localStorage?.setItem(LOCALE_KEY, locale);
};

/**
 * Every call queued behind whatever is already in flight, so two callers can never race each
 * other's `intl.init(...)` and have completion ORDER (not call order) decide which locale wins.
 *
 * The concrete case: `index.tsx` fires an unawaited bootstrap call before the router even mounts,
 * using the persisted/default locale. A guest form redeems its invitation and, once the data
 * arrives, calls this again with the device- or form-based locale it actually wants. Both write
 * the same `react-intl-universal` singleton with no relationship to each other — if the redeem
 * happens to resolve unusually fast (a cached response) while the bootstrap call is unusually
 * slow (a busy main thread), the bootstrap call can finish SECOND and silently overwrite the
 * locale the form had already, correctly, settled on.
 *
 * Queuing behind call ORDER rather than completion order fixes this generally, for any two
 * callers, without either one needing to know the other exists: `index.tsx`'s call is always
 * invoked first (nothing else can run before the module that starts it finishes evaluating), so
 * it is always queued first, and every later call — this guest form's, a future one, the language
 * switcher's — is guaranteed to apply strictly after it.
 */
let queuedInit: Promise<unknown> = Promise.resolve();

export const initIntl = async (locale?: string | undefined) => {
  const run = () =>
    intl.init({
      locales,
      currentLocale: locale ?? getCurrentLocale()
    });
  // Chained behind the previous link regardless of whether IT failed — a rejection queued here
  // must never permanently block every call that comes after it.
  const settled = queuedInit.catch(() => undefined).then(run);
  queuedInit = settled.catch(() => undefined);
  await settled;
};

export const getTrans = (key: TranslationKey) => {
  return intl.get(key);
};

export const getTransWithParams = (
  key: TranslationKey,
  params: TransParams
) => {
  return intl.get(key, params);
};

export const getLocaleLanguage = (): AppLocale =>
  (intl.getInitOptions().currentLocale as AppLocale) || defaultLang.value;

export const getLocale = () => {
  const localeStr = getLocaleLanguage();
  if (localeStr?.includes(AppLocale.en_US)) {
    return allLangs[AppLocale.en_US].adapterLocale;
  }
  return allLangs[AppLocale.de_DE].adapterLocale;
};
